<template>
    <!--
        USER PREFERENCES

        A macOS System Settings style preferences dialog.
        Sections are listed down the left, the selected section's detail is shown on the right.

        Preferences are saved live - a checkbox is persisted the moment it is toggled, and a text
        field shortly after typing stops. Closing the dialog (Done, the X, ESC or the mask) flushes
        anything still pending, so there is no way to close and lose a change.

        To add a new preferences section:
            1. add the keys to js/user-prefs-keys.js
            2. add an entry to the `sections` array below
            3. add the field descriptors to `PREF_FIELDS` below
            4. create the section component under ./sections/ and render it in the detail pane
    -->
    <Modal
        :value="value"
        title="User Preferences"
        width="880"
        class-name="bntx-prefs-modal"
        @input="handleVisibilityChange">

        <div class="bntx-prefs-body" :class="{ 'bntx-prefs-body-narrow' : isNarrowLayout }">

            <!-- ============================================ -->
            <!--    SECTION LIST                              -->
            <!--    left rail when wide, whole dialog when     -->
            <!--    narrow (until a section is drilled into)   -->
            <!-- ============================================ -->
            <div class="bntx-prefs-section-list"
                 v-show="isNarrowLayout == false || isShowingSectionDetail == false">

                <div v-for="section in sections"
                     :key="section.id"
                     :id="'user_prefs_section_' + section.id"
                     class="bntx-prefs-section-row"
                     :class="{ 'bntx-prefs-section-row-active' : isNarrowLayout == false && section.id == selectedSectionID }"
                     @click="handleSectionSelected(section.id)">

                    <span>
                        <font-awesome-icon :icon="section.icon" size="lg" pull="left" fixed-width />
                        {{ section.label }}
                    </span>

                    <Icon v-show="isNarrowLayout" type="ios-arrow-forward" size="18" />
                </div>
            </div>

            <!-- ============================================ -->
            <!--    SECTION DETAIL                            -->
            <!--    right pane when wide, whole dialog when    -->
            <!--    narrow and drilled into                    -->
            <!-- ============================================ -->
            <div class="bntx-prefs-section-detail"
                 v-show="isNarrowLayout == false || isShowingSectionDetail">

                <!-- narrow only - step back out to the section list -->
                <div v-show="isNarrowLayout"
                     id="user_prefs_back"
                     class="bntx-prefs-back"
                     @click="isShowingSectionDetail = false">

                    <Icon type="ios-arrow-back" size="18" /> Back
                </div>

                <!-- hold the controls back until the saved values have landed, so the user
                     cannot start editing a field that is about to be overwritten by the load -->
                <div v-show="isLoading" class="bntx-prefs-loading">
                    <Spin size="large" />
                </div>

                <UserPreferencesReport v-show="isLoading == false && selectedSectionID == 'report'"
                                       :preferences="preferences.report"
                                       @on-pref-change="handlePrefChange" />
            </div>
        </div>

        <div slot="footer">
            <span class="bntx-prefs-save-status">{{ saveStatusMessage }}</span>
            <Button id="user_prefs_done" type="primary" @click="handleDone">Done</Button>
        </div>
    </Modal>
</template>

<script>

const {itBnTx} = require('@/js/itBnTx')

var _ = require('lodash');

import UserPreferencesReport from './sections/report';
import { USER_PREF, prefValueToBool, boolToPrefValue } from '@/js/user-prefs-keys';

// how long after typing stops before a text preference is written back
const TEXT_PREF_SAVE_DEBOUNCE_MS = 800;

// below this width the two panes will not both fit, so the dialog switches to
// a drill-down: the section list fills the dialog, and picking a section replaces
// it with that section's detail plus a back step.
const NARROW_LAYOUT_MEDIA_QUERY = '(max-width: 640px)';

/*
    Ties each field on the `preferences` object to the preference it is stored under.
    `type` drives both the "0"/"1" conversion and how eagerly the value is saved -
    a boolean is persisted immediately, text is debounced while the user types.
*/
const PREF_FIELDS = [
    { section: 'report', field: 'autoIncludeEmgComplianceText',        pref: USER_PREF.REPORT_AUTO_INCLUDE_EMG_COMPLIANCE_TEXT,        type: 'boolean' },
    { section: 'report', field: 'emgComplianceText',                   pref: USER_PREF.REPORT_EMG_COMPLIANCE_TEXT,                     type: 'text'    },
    { section: 'report', field: 'autoIncludeUltrasoundComplianceText', pref: USER_PREF.REPORT_AUTO_INCLUDE_ULTRASOUND_COMPLIANCE_TEXT, type: 'boolean' },
    { section: 'report', field: 'ultrasoundComplianceText',            pref: USER_PREF.REPORT_ULTRASOUND_COMPLIANCE_TEXT,              type: 'text'    },
    { section: 'report', field: 'autoIncludeEmgAndUltrasoundComplianceText', pref: USER_PREF.REPORT_AUTO_INCLUDE_EMG_AND_ULTRASOUND_COMPLIANCE_TEXT, type: 'boolean' },
    { section: 'report', field: 'emgAndUltrasoundComplianceText',            pref: USER_PREF.REPORT_EMG_AND_ULTRASOUND_COMPLIANCE_TEXT,              type: 'text'    },
];

export default {
    name: 'user-preferences',

    components: {
        UserPreferencesReport
    },

    props: {
        // visibility of the dialog, driven by the parent via v-model
        value: {
            type: Boolean,
            default: false
        }
    },

    data () {
        return {
            selectedSectionID : 'report',

            // narrow (drill-down) layout, driven by NARROW_LAYOUT_MEDIA_QUERY
            isNarrowLayout : false,

            // narrow layout only - false shows the section list, true shows the selected
            // section's detail. Ignored when the layout is wide enough to show both.
            isShowingSectionDetail : false,

            isLoading : false,

            saveStatusMessage : '',

            sections : [
                { id: 'report', label: 'Report', icon: ['fal', 'clipboard-medical'] }
            ],

            preferences : {
                report : {
                    autoIncludeEmgComplianceText        : true,
                    emgComplianceText                   : '',
                    autoIncludeUltrasoundComplianceText : true,
                    ultrasoundComplianceText            : '',
                    autoIncludeEmgAndUltrasoundComplianceText : true,
                    emgAndUltrasoundComplianceText            : ''
                }
            },

            // the raw "as stored" value of each field at the time it was last loaded or saved,
            // so we only write back the ones which actually changed.
            lastSavedPrefValues : {},

            // one debounced save per text field, so typing in one field cannot cancel
            // a pending save of the other.
            debouncedSaveByField : {}
        }
    },

    created () {
        PREF_FIELDS.filter(inDescriptor => inDescriptor.type == 'text')
                   .forEach(inDescriptor => {
                        this.debouncedSaveByField[inDescriptor.field] = _.debounce(() => { this.savePref(inDescriptor) },
                                                                                  TEXT_PREF_SAVE_DEBOUNCE_MS)
                   })
    },

    mounted () {
        // NOTE: kept off `data` on purpose, a MediaQueryList does not need to be reactive.
        this.narrowLayoutMediaQuery = window.matchMedia(NARROW_LAYOUT_MEDIA_QUERY)

        this.handleNarrowLayoutChange(this.narrowLayoutMediaQuery)

        if (this.narrowLayoutMediaQuery.addEventListener != undefined) {
            this.narrowLayoutMediaQuery.addEventListener('change', this.handleNarrowLayoutChange)
        } else {
            // older browsers only have the deprecated listener API
            this.narrowLayoutMediaQuery.addListener(this.handleNarrowLayoutChange)
        }
    },

    beforeDestroy () {
        if (this.narrowLayoutMediaQuery == undefined) {
            return
        }

        if (this.narrowLayoutMediaQuery.removeEventListener != undefined) {
            this.narrowLayoutMediaQuery.removeEventListener('change', this.handleNarrowLayoutChange)
        } else {
            this.narrowLayoutMediaQuery.removeListener(this.handleNarrowLayoutChange)
        }
    },

    methods: {

        handleNarrowLayoutChange (inMediaQuery) {
            this.isNarrowLayout = inMediaQuery.matches
        },

        handleSectionSelected (inSectionID) {
            this.selectedSectionID = inSectionID

            // narrow layout drills in to the section, wide layout already shows it alongside
            this.isShowingSectionDetail = true
        },

        findPrefField (inFieldName) {
            return PREF_FIELDS.find(inDescriptor => inDescriptor.field == inFieldName)
        },

        // the value of a field, converted to how it is stored server side (always a string)
        prefValueForField (inDescriptor) {
            var currentValue = this.preferences[inDescriptor.section][inDescriptor.field]

            return inDescriptor.type == 'boolean' ? boolToPrefValue(currentValue) : currentValue
        },

        ////////////////////
        // LOAD
        ////////////////////

        loadPreferences () {
            this.isLoading = true
            this.saveStatusMessage = ''

            // NOTE: getUserPref creates the key with inDefaultValue when the user has never set it,
            //       so the documented default in user-prefs-keys.js is what a new user gets.
            Promise.all(PREF_FIELDS.map(inDescriptor => {

                return itBnTx.getUserPref({      inPrefKey : inDescriptor.pref.key,
                                            inDefaultValue : inDescriptor.pref.defaultValue })
                             .then(response => {
                                 var loadedValue = response.data.value

                                 this.lastSavedPrefValues[inDescriptor.field] = loadedValue

                                 this.preferences[inDescriptor.section][inDescriptor.field] =
                                        inDescriptor.type == 'boolean' ? prefValueToBool(loadedValue) : loadedValue
                             })
            }))
            .then(() => {
                this.isLoading = false
            })
            .catch(error => {
                console.log("loadPreferences failed", error)

                this.isLoading = false
                this.$Message.error('Could not load your preferences. Please try again.')

                // nothing was loaded reliably, so close rather than let the user edit and save
                // values which may not reflect what is actually stored.
                this.$emit('input', false)
            })
        },

        ////////////////////
        // SAVE
        ////////////////////

        handlePrefChange (inFieldName) {
            var descriptor = this.findPrefField(inFieldName)

            if (descriptor == undefined) {
                return
            }

            if (descriptor.type == 'text') {
                // wait for the user to stop typing before writing back
                this.debouncedSaveByField[inFieldName]()
            } else {
                this.savePref(descriptor)
            }
        },

        savePref (inDescriptor) {
            var valueToSave = this.prefValueForField(inDescriptor)

            // nothing actually changed, so nothing to write
            if (valueToSave === this.lastSavedPrefValues[inDescriptor.field]) {
                return Promise.resolve()
            }

            this.saveStatusMessage = 'Saving...'

            return itBnTx.updateUserPref({   inPrefKey : inDescriptor.pref.key,
                                           inPrefValue : valueToSave })
                         .then(() => {
                             this.lastSavedPrefValues[inDescriptor.field] = valueToSave
                             this.saveStatusMessage = 'Saved'
                         })
                         .catch(error => {
                             console.log("savePref failed", inDescriptor.pref.key, error)

                             // leave lastSavedPrefValues alone so the change is still considered
                             // unsaved, and will be retried on the next change or on close.
                             this.saveStatusMessage = ''
                             this.$Message.error('Could not save your preference. Please try again.')
                         })
        },

        // write back anything still sitting in a debounce, and anything a previous save failed on
        flushPendingSaves () {
            Object.values(this.debouncedSaveByField).forEach(inDebouncedSave => inDebouncedSave.cancel())

            return Promise.all(PREF_FIELDS.map(inDescriptor => this.savePref(inDescriptor)))
        },

        ////////////////////
        // DIALOG
        ////////////////////

        // closed via the X, ESC or the mask - preferences save live, so this saves too.
        handleVisibilityChange (isVisible) {
            if (isVisible == false) {
                this.flushPendingSaves()
            }

            this.$emit('input', isVisible);
        },

        handleDone () {
            this.flushPendingSaves()

            this.$emit('input', false);
        }
    },

    watch: {

        value : {
            // immediate, so the load also happens when the dialog is mounted already visible,
            // not only when it is toggled open.
            immediate : true,

            handler (newValue) {
                if (newValue == true) {
                    // always open on the section list when narrow, rather than wherever
                    // the user happened to be the last time the dialog was open.
                    this.isShowingSectionDetail = false

                    this.loadPreferences()
                }
            }
        }
    }
}
</script>

<style>

    .bntx-prefs-modal .ivu-modal-body {
        padding: 0;
    }

    /*
        Tracks the viewport rather than a fixed height, so a tall screen shows the whole Report
        section without scrolling and a short one scrolls the detail pane instead of overflowing
        the screen. 240px covers the modal's top offset, header and footer.
    */
    .bntx-prefs-body {
        display: flex;
        height: calc(100vh - 240px);
        min-height: 480px;
        max-height: 720px;
    }

    /* ---------- section list (left) ---------- */

    .bntx-prefs-section-list {
        width: 220px;
        flex: 0 0 220px;
        overflow-y: auto;
        padding: 8px 0;
        background: #f8fafc;
        border-right: 1px solid #e8eaec;
    }

    .bntx-prefs-section-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 8px 24px;
        cursor: pointer;
        color: #495060;
        transition: background .2s ease, color .2s ease;
    }

    .bntx-prefs-section-row:hover {
        background: #eaf6fd;
    }

    .bntx-prefs-section-row-active,
    .bntx-prefs-section-row-active:hover {
        color: #334155;
        background: #4ceafb;
    }

    /* ---------- section detail (right) ---------- */

    .bntx-prefs-section-detail {
        flex: 1 1 auto;
        overflow-y: auto;
        padding: 24px 28px;
    }

    .bntx-prefs-loading {
        display: flex;
        align-items: center;
        justify-content: center;
        height: 100%;
    }

    .bntx-prefs-detail-title {
        font-size: 18px;
        font-weight: 600;
        color: #334155;
        padding-bottom: 12px;
        margin-bottom: 20px;
        border-bottom: 1px solid #e8eaec;
    }

    .bntx-prefs-detail-hint {
        font-size: 12px;
        color: #80848f;
        margin: 6px 0 0 24px;
    }

    .bntx-prefs-field-label {
        font-weight: 600;
        color: #495060;
        margin: 16px 0 8px 0;
    }

    /* a field label with its "Reset to default" button sitting beside it */
    .bntx-prefs-field-label-row {
        display: flex;
        align-items: center;
        gap: 12px;
        margin: 16px 0 8px 0;
    }

    .bntx-prefs-field-label-row .bntx-prefs-field-label {
        margin: 0;
    }

    .bntx-prefs-section-divider {
        margin: 20px 0;
        border-top: 1px solid #e8eaec;
    }

    .bntx-prefs-save-status {
        float: left;
        font-size: 12px;
        color: #80848f;
        line-height: 32px;
    }

    /* ---------- narrow / drill-down layout ---------- */

    .bntx-prefs-back {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        margin-bottom: 16px;
        color: #2d8cf0;
        cursor: pointer;
        user-select: none;
    }

    /*
        Narrow: the list and the detail each take the whole dialog, one at a time.
        The dialog is also allowed to grow with the viewport rather than sitting at a
        fixed 540px, so short screens do not end up scrolling inside a scroll.
    */
    .bntx-prefs-body-narrow {
        flex-direction: column;
        height: calc(100vh - 190px);
        min-height: 320px;
        max-height: 540px;
    }

    .bntx-prefs-body-narrow .bntx-prefs-section-list {
        width: auto;
        flex: 1 1 auto;
        padding: 0;
        background: #ffffff;
        border-right: none;
    }

    .bntx-prefs-body-narrow .bntx-prefs-section-row {
        /* roomier rows, so they are a comfortable tap target */
        padding: 14px 20px;
        border-bottom: 1px solid #e8eaec;
    }

    .bntx-prefs-body-narrow .bntx-prefs-section-detail {
        padding: 16px 20px;
    }

    /* the hint sits under its checkbox, the extra indent is wasted width when narrow */
    .bntx-prefs-body-narrow .bntx-prefs-detail-hint {
        margin-left: 0;
    }

</style>
